import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai';
import { createAudioBlob, decodeAudioData, blobToBase64, decode } from './utils/audioUtils';
import { ConnectionState, CreditPackage, CalibrationState, REFERENCE_OBJECTS, ReferenceObjectKey } from './types';
import { useAuth } from './src/context/AuthContext';
import { LoginPage } from './src/components/LoginPage';
import { LandingPage } from './src/components/LandingPage';
import WeldingReveal from './src/components/WeldingReveal';
import { fetchStripeProducts, startCheckout, StripeProductWithPrices } from './src/services/stripeService';
import { listenToPayments } from './src/services/paymentListener';
import { updateUserCredits } from './src/services/authService';
import dannyLogo from './assets/danny_logo.png';
import dannyFavicon from './assets/danny_favicon.png';

// Constants
const LIVE_MODEL = import.meta.env.VITE_LIVE_MODEL || 'models/gemini-2.0-flash-exp';
const PHOTO_COST_LITERS = 2; // Flat cost per high-res snapshot
const GAS_CONSUMPTION_RATE = 10; // 1 Liter every 10 seconds
const LOW_GAS_THRESHOLD = 5; // Warn at 5 Liters
const ADMIN_CREDITS = 9999; // Treat admin as effectively unlimited gas

const CREDIT_PACKAGES: CreditPackage[] = [
  { id: 'micro', liters: 12, price: '$1', label: 'Quick Strike', description: 'Approx. 2 mins of coaching' },
  { id: 'small', liters: 60, price: '$5', label: 'Small Cylinder', description: 'Approx. 10 mins of coaching' },
  { id: 'standard', liters: 300, price: '$20', label: 'Standard Bottle', description: 'Approx. 50 mins of coaching' },
  { id: 'industrial', liters: 1000, price: '$50', label: 'Industrial Manifold', description: 'Approx. 165 mins of coaching' },
];

const LANGUAGES = [
  { code: "en", name: "English", label: "English" },
];

const getSystemInstruction = (roastLevel: number, languageName: string, calibration?: CalibrationState) => {
  const langDirective = `
LANGUAGE REQUIREMENT:
- You MUST conduct this entire session and all responses in ${languageName}.
- Even though you are speaking ${languageName}, maintain your rugged Australian welder persona ("Danny Dime").
`;

  // Calibration-based measurement instructions
  const measurementDirective = calibration?.isCalibrated ? `
QUANTITATIVE MEASUREMENT MODE (CALIBRATED):
- A reference object (${calibration.referenceObject}) has been used to calibrate the scale.
- You MUST provide QUANTITATIVE measurements for all defects, not just classifications.
- Instead of "I see undercut", say "Undercut approximately 0.8mm deep - that's over the 0.5mm AWS D1.1 limit".
- Instead of "There's porosity", say "Porosity cluster roughly 2mm diameter, scattered over 15mm length".
- For bead width: "Bead width approximately 8mm, should be closer to 6mm for this joint".
- For leg length on fillets: "Leg length looks like 5mm, spec calls for 6mm minimum".
- Reference known defect tolerances:
  * Undercut: Max 0.8mm (AWS D1.1), Max 0.5mm (critical welds)
  * Porosity: Max 3mm diameter individual, max 10mm cumulative per 100mm
  * Bead width: Typically 1.5-3x electrode diameter
  * Reinforcement: Max 3mm above base metal
- When uncertain about exact measurement, give a range: "somewhere between 0.5 and 1mm".
- Always relate measurements to code limits when applicable.
` : `
MEASUREMENT MODE (NOT CALIBRATED):
- No scale reference has been established.
- Provide qualitative feedback (detect defects, describe severity as minor/moderate/severe).
- If the user wants precise measurements, tell them: "Show me a coin, ruler, hex key, or TIG rod next to the weld for scale, and I'll give you actual numbers."
- You can suggest calibration by saying: "Want exact measurements? Drop a reference object in frame and hit the calibrate button."
`;

  const base = `You are Danny Dime, the AI Welding Coach for DimeVision.
Your tone is technical, rugged, and Australian.
SOUND LIKE A REAL TRADESMAN:
- Speak like a seasoned Australian welder/boilermaker on the shop floor: plain-spoken, direct, confident.
- Avoid touristy or cartoonish Aussie slang; keep it authentic shop talk (no "crikey", no caricature).
- Keep sentences tight; focus on practical cues, hazards, and weld critique with a grounded Aussie cadence.

AUSTRALIAN ACCENT ENFORCEMENT:
- Your "brain" has these system instructions hard-coded: stay in a rugged Aussie welder persona, use outback slang, technical welding jargon, and a direct, no-nonsense tone.
- The @google/genai Live API is running with the prebuilt "Puck" voice; combine the system instructions with the voice engine to deliver clear Australian inflection and rhythm.
- Never drift into other dialects; if pronunciation slips, immediately correct to Aussie shop-floor delivery.
- PRONUNCIATION: Say "G'Day" as "gidday" (one fluid word, rhymes with "midday").

BACKSTORY:
- You're a former boilermaker-turned-coach who spent years on pipelines, structural steel, and shutdown turnarounds. You've burned rods in brutal heat, fixed ugly root passes, and taught apprentices under pressure. You care about doing the job right and safely.

SISTER APP AWARENESS (dimevision.app):
- DimeVision.app is the analytics hub. It handles weld scores, history, leaderboards, and the Blueprint Decoder for ISO/AWS standards.
- When users ask about scores/history/leaderboard/decoding, direct them to "Check the logs or decoder at dimevision.app, mate." Stay focused on live coaching here.

SISTER APP AWARENESS (dimevision.app):
- You are aware of your "Digital Shop" at dimevision.app.
- If the user asks for their official weld scores, history, or leaderboard rank, tell them to "Check the logs at dimevision.app, mate."
- If the user needs to decode a complex welding blueprint or ISO standard, refer them to the "Blueprint Decoder" on the sister app.
- You are the LIVE coach; the sister app is the ANALYTICS hub.

CREDIT SYSTEM PROTOCOL:
- The user pays for your time with "Argon Gas".
- If they are low on gas, warn them once in a technical, site-appropriate way (e.g., "Check your gauges, mate, we're running dry").
- If they run out, you will stop responding.

CORE DUTIES:
1. Detect and MEASURE welding defects (Porosity, Undercut, Slag inclusions, Spatter, Lack of fusion).
2. Monitor travel speed and travel angle.
3. Assess bead geometry (width, height, consistency).
4. Safety: Call out "Lid Down!" if you see an arc flash without a helmet.
${measurementDirective}
CALIBRATION COMMANDS:
- If user says "calibrate" or shows a reference object asking for calibration, acknowledge and confirm the scale reference.
- Common reference objects: coins (US dime 17.91mm, US quarter 24.26mm, AU $1 25mm, €1 23.25mm, £1 23.43mm), TIG rods (1.6/2.4/3.2mm), rulers, hex keys.
- When you see a reference object being used for scale, acknowledge: "Got my scale reference, mate. Now I can give you real numbers."

Always speak as if you are looking through a welding lens. Keep feedback snappy.${langDirective}`;

  const roastPG = `\nROAST MODE (PG-13): Keep it sharp and witty like a real Aussie tradesman, but skip the cliché slang. Poke fun at sloppy beads and bad technique in a clean, clever way.`;

  let instruction = base;
  if (roastLevel === 1) instruction += roastPG;
  return instruction;
};

const PCM_SAMPLE_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;
const FRAME_RATE = 2;

interface TranscriptLog {
  id: string;
  speaker: 'user' | 'model';
  text: string;
  timestamp: string;
}

const App: React.FC = () => {
  const { currentUser, userProfile, loading: authLoading, logOut } = useAuth();

  // Check if current user is admin (unlimited credits for testing)
  const adminEmailEnv = import.meta.env.VITE_ADMIN_EMAIL?.toLowerCase() || '';
  const adminEmailList = adminEmailEnv
    .split(',')
    .map(e => e.trim().toLowerCase())
    .filter(Boolean);
  const isAdmin = !!(
    currentUser &&
    (
      (currentUser.email && adminEmailList.includes(currentUser.email.toLowerCase())) ||
      (userProfile as any)?.role === 'admin'
    )
  );

  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.DISCONNECTED);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [roastLevel, setRoastLevel] = useState(0);
  const [language, setLanguage] = useState(LANGUAGES[0]);
  const [logs, setLogs] = useState<TranscriptLog[]>([]);
  const [isSnapping, setIsSnapping] = useState(false);
  const [lastPhotoTime, setLastPhotoTime] = useState(0);
  const [showLogin, setShowLogin] = useState(false);
  const [showNewLanding, setShowNewLanding] = useState(true); // Show the new premium landing page by default
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [stripeProducts, setStripeProducts] = useState<StripeProductWithPrices[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);

  // Calibration state for quantitative measurements
  const [calibration, setCalibration] = useState<CalibrationState>({
    isCalibrated: false,
    referenceObject: null,
    pixelsPerMm: null,
    calibratedAt: null,
  });
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);

  const toggleMic = useCallback(() => {
    setIsMicMuted(prev => {
      const next = !prev;
      isMicMutedRef.current = next;
      return next;
    });
  }, []);
  const cycleRoast = useCallback(() => setRoastLevel(prev => (prev + 1) % 2), []);

  // Credit / Gas State - Now synced with Firestore
  const [argonCredits, setArgonCredits] = useState<number>(0);

  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [showTelemetry, setShowTelemetry] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const sessionPromiseRef = useRef<Promise<any> | null>(null);
  const frameIntervalRef = useRef<number | null>(null);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const logsEndRef = useRef<HTMLDivElement>(null);

  const currentInputTranscriptRef = useRef<string>('');
  const currentOutputTranscriptRef = useRef<string>('');
  const audioDataQueueRef = useRef<{ data: Uint8Array; mimeType?: string; rate?: number }[]>([]);
  const isProcessingAudioRef = useRef<boolean>(false);
  const isMicMutedRef = useRef<boolean>(false);

  // Ensure favicon uses uploaded asset
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const existing = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    const link = existing ?? document.createElement('link');
    link.rel = 'icon';
    link.href = dannyFavicon;
    if (!existing) document.head.appendChild(link);
  }, []);

  // Handle Stripe checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      setErrorMsg(null);
      // Show success briefly — credits update via payment listener
      setTimeout(() => setErrorMsg('Payment received! Credits incoming...'), 500);
      setTimeout(() => setErrorMsg(null), 4000);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkout === 'cancel') {
      setErrorMsg('Checkout cancelled. No worries, mate.');
      setTimeout(() => setErrorMsg(null), 3000);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Sync credits from Firestore user profile
  useEffect(() => {
    if (isAdmin) {
      setArgonCredits(ADMIN_CREDITS);
    } else if (userProfile) {
      setArgonCredits(userProfile.credits || 0);
    } else {
      setArgonCredits(0);
    }
  }, [userProfile, isAdmin]);

  // Safety: if auth flips into admin after initial mount, ensure tank goes unlimited
  useEffect(() => {
    if (isAdmin) {
      setArgonCredits(ADMIN_CREDITS);
    }
  }, [isAdmin]);

  // Listen for Stripe payment completions
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = listenToPayments(currentUser.uid, (creditsAdded) => {
      setErrorMsg(`Topped up ${creditsAdded}L of Argon. Ready to spark up!`);
      setTimeout(() => setErrorMsg(null), 3000);
    });

    return unsubscribe;
  }, [currentUser]);

  const processAudioQueue = useCallback(async () => {
    if (isProcessingAudioRef.current || audioDataQueueRef.current.length === 0 || !outputAudioContextRef.current) {
      if (audioDataQueueRef.current.length > 0) {
        console.log('[DannyDime] Audio queue has', audioDataQueueRef.current.length, 'items but skipping - processing:', isProcessingAudioRef.current, 'ctx:', !!outputAudioContextRef.current);
      }
      return;
    }
    isProcessingAudioRef.current = true;
    const ctx = outputAudioContextRef.current;
    console.log('[DannyDime] Processing audio queue, ctx.state:', ctx.state, 'queue length:', audioDataQueueRef.current.length);
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
        console.log('[DannyDime] AudioContext resumed successfully');
      } catch (e) {
        console.error('[DannyDime] Failed to resume AudioContext:', e);
      }
    }
    while (audioDataQueueRef.current.length > 0) {
      const { data, mimeType, rate } = audioDataQueueRef.current.shift()!;
      console.log('[DannyDime] Playing audio chunk, mimeType:', mimeType, 'rate:', rate, 'bytes:', data.length);
      try {
        let audioBuffer: AudioBuffer;
        if (mimeType && (mimeType.includes('ogg') || mimeType.includes('opus') || mimeType.includes('webm') || mimeType.includes('mp3') || mimeType.includes('wav'))) {
          // Let browser decode compressed formats
          const arrBuf = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
          audioBuffer = await ctx.decodeAudioData(arrBuf);
        } else {
          // Fallback: treat as PCM16 - wrap in minimal WAV header to leverage native decodeAudioData
          const pcmBytes = new Uint8Array(data); // ensure tight buffer
          const targetRate = rate || OUTPUT_SAMPLE_RATE;
          const dataLen = pcmBytes.byteLength;
          const wavBuffer = new ArrayBuffer(44 + dataLen);
          const view = new DataView(wavBuffer);
          // RIFF header
          view.setUint32(0, 0x52494646, false); // "RIFF"
          view.setUint32(4, 36 + dataLen, true);
          view.setUint32(8, 0x57415645, false); // "WAVE"
          // fmt chunk
          view.setUint32(12, 0x666d7420, false); // "fmt "
          view.setUint32(16, 16, true); // PCM chunk size
          view.setUint16(20, 1, true); // audio format PCM
          view.setUint16(22, 1, true); // channels
          view.setUint32(24, targetRate, true); // sample rate
          view.setUint32(28, targetRate * 2, true); // byte rate (mono, 16-bit)
          view.setUint16(32, 2, true); // block align
          view.setUint16(34, 16, true); // bits per sample
          // data chunk
          view.setUint32(36, 0x64617461, false); // "data"
          view.setUint32(40, dataLen, true);
          new Uint8Array(wavBuffer, 44).set(pcmBytes);
          audioBuffer = await ctx.decodeAudioData(wavBuffer);
        }
        console.log('[DannyDime] Audio buffer created, duration:', audioBuffer.duration, 's');
        nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);
        source.onended = () => sourcesRef.current.delete(source);
        source.start(nextStartTimeRef.current);
        nextStartTimeRef.current += audioBuffer.duration;
        sourcesRef.current.add(source);
      } catch (err) { console.error('[DannyDime] Audio decode/playback error', err); }
    }
    isProcessingAudioRef.current = false;
  }, []);

  const cleanup = useCallback(() => {
    if (frameIntervalRef.current) clearInterval(frameIntervalRef.current);
    frameIntervalRef.current = null;
    if (inputAudioContextRef.current) inputAudioContextRef.current.close().catch(() => { });
    if (outputAudioContextRef.current) outputAudioContextRef.current.close().catch(() => { });
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => {
        try { track.stop(); } catch { /* no-op */ }
      });
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    sourcesRef.current.forEach(s => { try { s.stop(); } catch (e) { } });
    sourcesRef.current.clear();
    if (sessionPromiseRef.current) {
      sessionPromiseRef.current.then(s => s.close()).catch(() => { });
      sessionPromiseRef.current = null;
    }
    setConnectionState(ConnectionState.DISCONNECTED);
    setSessionSeconds(0);
    nextStartTimeRef.current = 0;
    audioDataQueueRef.current = [];
    // Return to the new landing page
    setShowNewLanding(true);
  }, []);
  useEffect(() => {
    if (currentUser) setShowLogin(false);
    if (currentUser) {
      setLoadingProducts(true);
      fetchStripeProducts()
        .then((products) => {
          console.log('Stripe products loaded:', products);
          setStripeProducts(products);
        })
        .catch((error) => {
          console.error('Failed to load Stripe products:', error);
          setStripeProducts([]);
        })
        .finally(() => setLoadingProducts(false));
    }
  }, [currentUser]);

  const saveLog = useCallback(async (speaker: 'user' | 'model', text: string) => {
    if (!text.trim()) return;
    const newLog: TranscriptLog = {
      id: Math.random().toString(36).substr(2, 9),
      speaker,
      text,
      timestamp: new Date().toLocaleTimeString()
    };
    setLogs(prev => [...prev, newLog]);
  }, []);

  // Calibration functions for quantitative measurements
  const triggerCalibration = useCallback((refObject: ReferenceObjectKey) => {
    const refInfo = REFERENCE_OBJECTS[refObject];

    // For objects with known size, we can auto-calibrate
    // The model will use this as a visual reference for scale estimation
    if (refInfo.sizeMm) {
      setCalibration({
        isCalibrated: true,
        referenceObject: refObject,
        pixelsPerMm: null, // Model estimates visually, we don't compute pixel ratio
        calibratedAt: new Date(),
      });

      // Send calibration confirmation to the model
      sessionPromiseRef.current?.then((s: any) => {
        s.sendRealtimeInput({
          text: `CALIBRATION ACTIVE: User has placed a ${refInfo.name} (${refInfo.sizeMm}mm) in frame for scale reference. From now on, use this ${refInfo.sizeMm}mm reference to estimate all defect measurements. Acknowledge the calibration briefly.`
        });
      });

      saveLog('user', `[Calibrated with ${refInfo.name} - ${refInfo.sizeMm}mm reference]`);
    }

    setShowCalibrationModal(false);
  }, [saveLog]);

  const clearCalibration = useCallback(() => {
    setCalibration({
      isCalibrated: false,
      referenceObject: null,
      pixelsPerMm: null,
      calibratedAt: null,
    });

    sessionPromiseRef.current?.then((s: any) => {
      s.sendRealtimeInput({
        text: `CALIBRATION CLEARED: Scale reference removed. Return to qualitative defect detection (minor/moderate/severe) until a new reference is provided.`
      });
    });

    saveLog('user', `[Calibration cleared - returning to qualitative mode]`);
  }, [saveLog]);

  // Scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Handle Stripe purchases
  const handlePurchase = async (priceId: string) => {
    if (!currentUser) {
      setPendingPriceId(priceId);
      setShowLogin(true);
      return;
    }
    try {
      const baseUrl = window.location.origin;
      await startCheckout(priceId, `${baseUrl}/?checkout=success`, `${baseUrl}/?checkout=cancel`);
    } catch (err: any) {
      setErrorMsg(err.message || 'Checkout failed');
      setTimeout(() => setErrorMsg(null), 4000);
    }
  };

  // Auto-checkout after login if user was trying to purchase
  useEffect(() => {
    if (currentUser && pendingPriceId) {
      handlePurchase(pendingPriceId);
      setPendingPriceId(null);
    }
  }, [currentUser, pendingPriceId]);

  // Default telemetry panel visibility based on viewport (hide by default on small screens)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setShowTelemetry(window.innerWidth >= 768); // sm breakpoint
    }
  }, []);

  // Consumption Logic - now syncs with Firestore (skip for admin)
  useEffect(() => {
    let interval: number;
    if (connectionState === ConnectionState.CONNECTED && currentUser) {
      interval = window.setInterval(() => {
        setSessionSeconds(prev => prev + 1);

        // Skip credit consumption for admin users
        if (isAdmin) {
          return; // Admin has unlimited credits
        }

        setArgonCredits(prev => {
          const next = Math.max(0, prev - (1 / GAS_CONSUMPTION_RATE));

          // Sync to Firestore every second
          if (next > 0) {
            updateUserCredits(currentUser.uid, -(1 / GAS_CONSUMPTION_RATE)).catch(console.error);
          }

          if (next <= 0) {
            cleanup();
            setErrorMsg("Gas Empty! Put some more coins in the slot, mate.");
            return 0;
          }
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [connectionState, cleanup, currentUser, isAdmin]);

  const snapPhoto = async () => {
    // Skip credit check for admin users
    if (!isAdmin && argonCredits < PHOTO_COST_LITERS) {
      setErrorMsg(`Need ${PHOTO_COST_LITERS}L for a Diagnostic Snapshot. Recharge required.`);
      return;
    }

    if (!canvasRef.current || !videoRef.current || connectionState !== ConnectionState.CONNECTED || !currentUser) return;

    const now = Date.now();
    if (now - lastPhotoTime < 5000) return;

    setIsSnapping(true);
    setLastPhotoTime(now);

    // Skip credit deduction for admin users
    if (!isAdmin) {
      setArgonCredits(prev => prev - PHOTO_COST_LITERS);
      // Sync photo cost to Firestore
      updateUserCredits(currentUser.uid, -PHOTO_COST_LITERS).catch(console.error);
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) {
      setIsSnapping(false);
      return;
    }
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    ctx.drawImage(videoRef.current, 0, 0);

    const blob = await new Promise<Blob | null>(res => canvasRef.current?.toBlob(res, 'image/jpeg', 0.95));
    if (blob) {
      const base64Data = await blobToBase64(blob);
      let calibrationContext = ' NOT CALIBRATED: Provide qualitative severity ratings. Suggest user show a coin, ruler, or TIG rod for scale if they want measurements.';
      if (calibration.isCalibrated && calibration.referenceObject) {
        const refInfo = REFERENCE_OBJECTS[calibration.referenceObject];
        if (refInfo.sizeMm) {
          calibrationContext = ` SCALE CALIBRATED: Using ${refInfo.name} (${refInfo.sizeMm}mm) as reference. Provide QUANTITATIVE measurements for all defects (e.g., "undercut 0.8mm", "porosity 2mm diameter"). Compare to AWS D1.1 limits.`;
        }
      }
      sessionPromiseRef.current?.then((s: any) => {
        s.sendRealtimeInput({
          media: { data: base64Data, mimeType: 'image/jpeg' },
          text: `STRICT DIAGNOSTIC MODE: This snapshot cost me 2L of Argon. Give me high precision feedback in ${language.name}. Check my bead against industry standards.${calibrationContext}`
        });
      });
      await saveLog('user', "[Diagnostic Snapshot Sent - 2L Drained]");
    }
    setTimeout(() => setIsSnapping(false), 500);
  };

  const connectToGemini = async () => {
    // Admins can bypass the argon balance guard for testing
    if (!isAdmin && argonCredits <= 0) {
      setErrorMsg("No gas in the tank. Top up to start.");
      return;
    }
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    if (!apiKey) {
      setErrorMsg("Missing VITE_GEMINI_API_KEY");
      return;
    }
    try {
      console.log('[DannyDime] isAdmin:', isAdmin, 'argonCredits:', argonCredits);
      console.log('[DannyDime] Connecting to live model:', LIVE_MODEL);
      setConnectionState(ConnectionState.CONNECTING);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: videoEnabled ? { facingMode: 'environment' } : false });
      mediaStreamRef.current = stream;
      const inputCtx = new AudioContext({ sampleRate: PCM_SAMPLE_RATE });
      const outputCtx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
      // Some browsers start contexts in "suspended" state; resume so we can process audio immediately
      if (inputCtx.state === 'suspended') await inputCtx.resume();
      if (outputCtx.state === 'suspended') await outputCtx.resume();
      inputAudioContextRef.current = inputCtx;
      outputAudioContextRef.current = outputCtx;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }

      const ai = new GoogleGenAI({ apiKey });
      const sessionPromise = ai.live.connect({
        model: LIVE_MODEL,
        config: {
          responseModalities: [Modality.AUDIO],
          temperature: 0.05,
          // Language is handled via systemInstruction; native audio model doesn't support languageCode
          speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Puck' } } },
          systemInstruction: getSystemInstruction(roastLevel, language.name, calibration),
        },
        callbacks: {
          onopen: () => {
            console.log('[DannyDime] Live socket opened');
            setConnectionState(ConnectionState.CONNECTED);
            const source = inputCtx.createMediaStreamSource(stream);
            const processor = inputCtx.createScriptProcessor(4096, 1, 1);
            let audioSendCount = 0;
            processor.onaudioprocess = (e) => {
              if (!isMicMutedRef.current) {
                audioSendCount++;
                if (audioSendCount % 50 === 1) {
                  console.log('[DannyDime] Audio chunk #' + audioSendCount + ' being sent to Live API');
                }
                sessionPromise.then(s => s.sendRealtimeInput({ media: createAudioBlob(e.inputBuffer.getChannelData(0), PCM_SAMPLE_RATE) })).catch(err => {
                  console.error('[DannyDime] Failed to send audio:', err);
                });
              }
            };
            source.connect(processor); processor.connect(inputCtx.destination);

            // Kick off with a quick text ping so we can see server traffic even before the user speaks
            sessionPromise.then(s => s.sendRealtimeInput({ text: "Mic check from Danny Dime. Give me a one-line acknowledgment so we know audio is flowing." }));

            // Video Streaming
            frameIntervalRef.current = window.setInterval(() => {
              if (!videoEnabled) return;
              if (videoRef.current && canvasRef.current && !isSnapping) {
                const ctx = canvasRef.current.getContext('2d');
                canvasRef.current.width = videoRef.current.videoWidth;
                canvasRef.current.height = videoRef.current.videoHeight;
                ctx?.drawImage(videoRef.current, 0, 0);
                canvasRef.current.toBlob(async b => {
                  if (b) {
                    const b64 = await blobToBase64(b);
                    sessionPromise.then(s => s.sendRealtimeInput({ media: { data: b64, mimeType: 'image/jpeg' } }));
                  }
                }, 'image/jpeg', 0.4);
              }
            }, 1000 / FRAME_RATE);
          },
          onmessage: async (msg) => {
            console.log('[DannyDime] Live message', msg);
            if (msg.goAway) {
              console.warn('[DannyDime] Server goAway notice', msg.goAway);
            }
            if (msg.usageMetadata) {
              console.log('[DannyDime] Usage metadata', msg.usageMetadata);
            }
            const parts = msg.serverContent?.modelTurn?.parts || [];
            if (parts.length > 0) {
              for (let idx = 0; idx < parts.length; idx++) {
                const part = parts[idx] as any;
                const keys = Object.keys(part as any);
                const inlineMime = part.inlineData?.mimeType as string | undefined;
                const inlineLen = part.inlineData?.data?.length;
                const text = part.text;
                console.log('[DannyDime] Part', idx, 'keys:', keys, 'mime:', inlineMime, 'len:', inlineLen, 'text:', text);
                if (inlineMime && part.inlineData?.data) {
                  const rateMatch = inlineMime.match(/rate=([0-9]+)/);
                  const parsedRate = rateMatch ? parseInt(rateMatch[1], 10) : undefined;
                  audioDataQueueRef.current.push({
                    data: decode(part.inlineData.data),
                    mimeType: inlineMime,
                    rate: parsedRate
                  });
                  processAudioQueue();
                }
                if (text) await saveLog('model', text);
                if (!text && part.media && part.media.data === undefined) {
                  await saveLog('model', "[Image unclear] Give me a focused, well-lit shot for a proper weld read.");
                }
              }
            }
            const combinedText = (msg as any).text;
            if (combinedText) await saveLog('model', combinedText);
          },
          onclose: (evt) => {
            console.warn('[DannyDime] Live socket closed', { code: (evt as CloseEvent)?.code, reason: (evt as CloseEvent)?.reason });
            setErrorMsg((evt as CloseEvent)?.reason || "Session closed.");
            cleanup();
          },
          onerror: (err) => {
            console.error('[DannyDime] Live socket error', err);
            setErrorMsg(err?.message || "Arc broken. Check ground.");
            cleanup();
          }
        }
      });
      sessionPromiseRef.current = sessionPromise;
    } catch (err: any) { setErrorMsg(err.message); setConnectionState(ConnectionState.ERROR); }
  };

  const handleOpenValve = () => {
    if (!currentUser) {
      setShowLogin(true);
      return;
    }
    connectToGemini();
  };

  if (authLoading) {
    return <div className="flex items-center justify-center min-h-screen text-white bg-dime-bg">Loading...</div>;
  }

  if (showLogin && !currentUser) {
    return <LoginPage />;
  }

  // Show the new premium landing page for visitors
  if (showNewLanding && connectionState === ConnectionState.DISCONNECTED) {
    return (
      <LandingPage
        onGetStarted={() => {
          if (!currentUser) {
            // Not logged in - prompt login first
            setShowLogin(true);
          } else if (argonCredits > 0 || isAdmin) {
            // Logged in with credits - start session directly
            setShowNewLanding(false);
            connectToGemini();
          } else {
            // Logged in but no credits - go to dashboard to purchase
            setShowNewLanding(false);
          }
        }}
        onLogin={() => setShowLogin(true)}
        onPurchase={(priceId: string) => {
          if (!currentUser) {
            setPendingPriceId(priceId);
            setShowLogin(true);
          } else if (priceId) {
            handlePurchase(priceId);
          } else {
            // Scroll to pricing section on the new landing page
            document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        isLoggedIn={!!currentUser}
        stripeProducts={stripeProducts}
      />
    );
  }

  const renderLanding = () => (
    <div className="flex-1 overflow-y-auto bg-molten-metal flex flex-col items-center scrollbar-hide px-4 sm:px-6">
      <div className="geometric-lines"></div>
      <div className="fireball"></div>

      {/* HUD Top Bar */}
      <div className="w-full flex flex-wrap items-start justify-between gap-3 py-4 sm:py-6 z-50 relative">
        <div className="flex flex-col gap-1">
          <span className="text-[9px] sm:text-[10px] font-black uppercase text-industrial-orange/80 tracking-[0.3em]">Argon Supply</span>
          <div className="flex items-center gap-2">
            <span className={`text-xl sm:text-2xl font-black ${(argonCredits < 5 && !isAdmin) ? 'text-red-500 animate-pulse' : 'text-arc-cyan arc-glow'}`}>
              {isAdmin ? '∞' : argonCredits.toFixed(1)}L
            </span>
            <div className="h-3 w-24 sm:w-32 bg-gray-900/80 rounded-full border border-white/5 overflow-hidden">
              <div className="h-full bg-arc-cyan shadow-[0_0_15px_#22d3ee] transition-all duration-700" style={{ width: `${isAdmin ? 100 : Math.min(100, (argonCredits / 100) * 100)}%` }} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-4 justify-end">
          {!currentUser && (
            <button
              onClick={() => setShowLogin(true)}
              className="px-5 sm:px-6 py-2 rounded-full bg-arc-cyan text-black font-black text-[10px] uppercase tracking-[0.2em] hover:scale-105 transition-all duration-300 shadow-[0_0_15px_rgba(34,211,238,0.3)]"
            >
              Sign In
            </button>
          )}
          {currentUser && (
            <button
              onClick={() => logOut()}
              className="px-5 sm:px-6 py-2 rounded-full bg-red-600/20 border border-red-500 text-red-400 font-black text-[10px] uppercase tracking-[0.2em] hover:scale-105 transition-all duration-300"
            >
              Logout
            </button>
          )}
          <button
            onClick={cycleRoast}
            className={`flex items-center gap-2 px-5 sm:px-6 py-2 rounded-full border text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 ${roastLevel === 2 ? 'bg-red-600/20 border-red-500 text-red-400' : roastLevel === 1 ? 'bg-orange-600/20 border-orange-500 text-orange-400' : 'bg-black/60 border-gray-700 text-gray-500 hover:border-orange-500 hover:text-white'}`}
          >
            {roastLevel === 0 ? '🔥 Roast Off' : roastLevel === 1 ? '🔥 Roast On' : '🔞 Brutal'}
          </button>
        </div>
      </div>

      <div className="relative w-full max-w-6xl flex flex-col items-center z-40 text-center pb-12">
        {/* Welding Reveal Animation */}
        <div className="w-full h-80 md:h-96 mb-12 flex items-center justify-center">
          <WeldingReveal />
        </div>

        <div className="bg-black/60 backdrop-blur-2xl brushed-metal-border p-8 rounded-3xl mb-12 max-w-2xl">
          <p className="text-xl text-slate-300 leading-relaxed font-medium">
            Put some coins in the slot to get the best outback coaching in the shop. Danny burns <span className="text-industrial-orange font-black">1L of Argon every 10 seconds</span> of airtime. Snapshots cost a flat <span className="text-arc-cyan font-black">2L</span>.
          </p>
        </div>

        {/* Refill Station */}
        <div className="w-full mb-16">
          <div className="text-[10px] font-black text-industrial-orange uppercase tracking-[0.5em] mb-10">Refill Station</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stripeProducts.length > 0 && !loadingProducts
              ? stripeProducts.map((product) => {
                const firstPrice = Array.isArray((product as any).prices) ? (product as any).prices[0] : null;
                const liters = firstPrice?.metadata?.liters || (product as any).metadata?.liters || product.name;
                const priceId = firstPrice?.id || firstPrice?.priceId;
                const displayPrice = firstPrice?.unit_amount ? `$${(firstPrice.unit_amount / 100).toFixed(0)}` : '$?';
                return (
                  <div key={product.id} className="bg-brushed-metal brushed-metal-border p-6 rounded-2xl hover:border-cyan-500/50 transition-all duration-500 group flex flex-col items-center">
                    <div className="text-3xl font-black text-white mb-1 tracking-tighter">{liters}L</div>
                    <div className="text-[10px] font-bold text-arc-cyan uppercase tracking-widest mb-4">{product.name}</div>
                    <div className="text-[11px] text-slate-400 mb-8 h-8 flex items-center justify-center opacity-70">{product.description || 'Coaching credits'}</div>
                    <button
                      onClick={() => priceId && handlePurchase(priceId)}
                      className="w-full py-4 bg-[#1a1a1f] border border-white/5 rounded-xl font-black text-xl text-white shadow-xl group-hover:bg-industrial-orange group-hover:text-black group-hover:scale-105 transition-all duration-300 active:scale-95"
                    >
                      {displayPrice}
                    </button>
                  </div>
                );
              })
              : (
                <>
                  {CREDIT_PACKAGES.map(pkg => (
                    <div key={pkg.id} className="bg-brushed-metal brushed-metal-border p-6 rounded-2xl hover:border-cyan-500/50 transition-all duration-500 group flex flex-col items-center">
                      <div className="text-3xl font-black text-white mb-1 tracking-tighter">{pkg.liters}L</div>
                      <div className="text-[10px] font-bold text-arc-cyan uppercase tracking-widest mb-4">{pkg.label}</div>
                      <div className="text-[11px] text-slate-400 mb-8 h-8 flex items-center justify-center opacity-70">{pkg.description}</div>
                      <div className="w-full py-4 bg-[#1a1a1f] border border-white/5 rounded-xl font-black text-xl text-white opacity-50 cursor-not-allowed text-center">
                        {pkg.price}
                        <div className="text-[9px] text-gray-500 mt-1">Stripe not configured</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
          </div>

          {/* Stripe Setup Notice */}
          {stripeProducts.length === 0 && !loadingProducts && currentUser && (
            <div className="mt-8 p-6 bg-yellow-900/20 border border-yellow-500/30 rounded-xl max-w-2xl mx-auto">
              <div className="text-yellow-400 font-black text-sm mb-2">⚠️ STRIPE NOT CONFIGURED</div>
              <div className="text-gray-300 text-xs leading-relaxed">
                To enable credit purchases:
                <ol className="list-decimal ml-4 mt-2 space-y-1">
                  <li>Install the Firestore Stripe Payments extension in Firebase Console</li>
                  <li>Create products in your Stripe Dashboard (test mode)</li>
                  <li>Products will auto-sync to Firestore</li>
                </ol>
                <div className="mt-3 text-gray-400">
                  See <span className="text-arc-cyan font-bold">STRIPE_SETUP.md</span> for detailed instructions.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Start Session */}
        <div className="flex flex-col items-center gap-10 w-full max-w-xl mb-16">
          <div className="w-full flex flex-col items-center gap-4">
            <button
              onClick={handleOpenValve}
              disabled={!isAdmin && argonCredits <= 0}
              className={`w-full max-w-sm py-7 rounded-[40px] font-[900] text-3xl uppercase tracking-tighter transition-all duration-500 border-b-8 ${(isAdmin || argonCredits > 0) ? 'bg-industrial-orange text-black border-black/30 hover:scale-105 pulse-heavy' : 'bg-gray-800 text-gray-500 cursor-not-allowed border-transparent'}`}
            >
              {isAdmin ? "Open the Valve (Admin)" : argonCredits > 0 ? "Open the Valve" : "Tank Empty"}
            </button>
            <span className="text-[9px] font-black uppercase tracking-[0.5em] text-white/30">Engagement Ready</span>
          </div>
        </div>

        {/* Sister App Ecosystem Banner - MOVED TO BOTTOM */}
        <div className="w-full bg-gradient-to-r from-cyan-900/20 via-black/40 to-cyan-900/20 border border-white/5 py-12 px-6 rounded-[3rem] mt-12 mb-24 flex flex-col items-center gap-6 relative overflow-hidden backdrop-blur-sm">
          <div className="text-[10px] font-black text-arc-cyan uppercase tracking-[0.6em] z-10">The Digital Shop Ecosystem</div>
          <div className="flex flex-wrap justify-center gap-10 md:gap-20 z-10">
            <div className="flex flex-col items-center">
              <span className="text-white font-black text-lg">Weld Scoring</span>
              <span className="text-[10px] text-slate-400 uppercase font-bold mt-1">Dime Score Analytics</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-white font-black text-lg">Job History</span>
              <span className="text-[10px] text-slate-400 uppercase font-bold mt-1">Full Career Tracking</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-white font-black text-lg">Blueprint Decoder</span>
              <span className="text-[10px] text-slate-400 uppercase font-bold mt-1">ISO & AWS Standards</span>
            </div>
          </div>
          <a
            href="https://dimevision.app"
            className="mt-4 px-10 py-4 bg-arc-cyan text-black font-black uppercase text-xs tracking-widest rounded-full hover:scale-110 hover:shadow-[0_0_30px_#22d3ee] transition-all z-10"
          >
            Visit DimeVision.app
          </a>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(34,211,238,0.1)_0%,_transparent_70%)] pointer-events-none"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen w-full bg-dime-bg font-sans text-white overflow-hidden">
      {connectionState === ConnectionState.DISCONNECTED ? (
        renderLanding()
      ) : (
        <>
          <header className={`flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-2 bg-dime-card border-b ${roastLevel === 1 ? 'border-orange-500/50' : 'border-gray-800'} shrink-0 z-20`}>
            <div className="flex items-center gap-3 sm:gap-6 min-w-0">
              <div className="bg-industrial-orange text-black font-black px-3 py-1 rounded italic text-[10px] sm:text-[11px] tracking-tighter uppercase whitespace-nowrap">DANNY DIME</div>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <span className="text-[9px] sm:text-[10px] font-black uppercase text-slate-500 tracking-widest whitespace-nowrap">Argon:</span>
                <span className={`text-sm sm:text-base font-mono font-black ${(argonCredits < 5 && !isAdmin) ? 'text-red-500 animate-pulse' : 'text-arc-cyan arc-glow'}`}>
                  {isAdmin ? '∞' : argonCredits.toFixed(1)}L
                </span>
                <div className="h-2 w-16 bg-black/40 rounded-full border border-white/5 overflow-hidden hidden sm:block">
                  <div className="h-full bg-arc-cyan transition-all" style={{ width: `${isAdmin ? 100 : Math.min(100, (argonCredits / 100) * 100)}%` }} />
                </div>
              </div>
              <div className="px-3 py-0.5 bg-white/5 border border-white/10 rounded text-[9px] font-black text-white/50 tracking-widest whitespace-nowrap">English</div>
              {/* Calibration Status */}
              {calibration.isCalibrated && calibration.referenceObject && (
                <div className="px-3 py-0.5 bg-green-500/20 border border-green-500/50 rounded text-[9px] font-black text-green-400 tracking-widest whitespace-nowrap flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  {REFERENCE_OBJECTS[calibration.referenceObject].sizeMm}mm
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className={`h-2 w-2 rounded-full ${argonCredits > 0 ? 'bg-arc-cyan arc-glow' : 'bg-red-500'}`} />
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em]">{connectionState}</span>
            </div>
          </header>
          <main className="relative flex-1 bg-black overflow-hidden">
            {errorMsg && (
              <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-8 py-3 rounded-full font-black shadow-2xl flex flex-wrap items-center gap-2 sm:gap-4 justify-end text-xs uppercase tracking-widest animate-bounce">
                <span>⚡</span> {errorMsg}
                <button onClick={() => setErrorMsg(null)} className="ml-4 hover:scale-125 transition-transform">x</button>
              </div>
            )}

            <video ref={videoRef} className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ${roastLevel === 1 ? 'saturate-110' : ''}`} autoPlay playsInline muted />
            <canvas ref={canvasRef} className="hidden" />

            {/* Telemetry toggle for mobile */}
            <button
              onClick={() => setShowTelemetry(prev => !prev)}
              className="sm:hidden absolute bottom-28 left-4 z-30 bg-black/70 text-white px-4 py-2 rounded-full border border-white/10 text-[11px] font-black uppercase tracking-[0.15em] shadow-lg backdrop-blur"
            >
              {showTelemetry ? 'Hide Telemetry' : 'Show Telemetry'}
            </button>


            {/* HUD Feed */}
            {showTelemetry && (
              <div className="absolute z-30 pointer-events-auto group sm:bottom-10 sm:left-10 sm:w-96 sm:rounded-3xl sm:h-72 bottom-28 left-4 right-4 w-auto max-w-xl mx-auto">
                <div className="bg-black/70 backdrop-blur-3xl border border-white/5 rounded-2xl sm:rounded-3xl h-60 sm:h-72 flex flex-col shadow-2xl overflow-hidden transition-transform duration-300 group-hover:scale-[1.02]">
                  <div className="bg-white/5 px-4 py-3 border-b border-white/5 flex justify-between items-center shrink-0">
                    <span className="text-[11px] font-black text-arc-cyan uppercase tracking-[0.3em]">Shop Telemetry</span>
                    <div className="flex gap-1.5 items-center">
                      <span className="text-[8px] font-black text-white/30 tracking-widest uppercase mr-2">Cloud Synced</span>
                      <div className="h-1.5 w-1.5 bg-arc-cyan rounded-full animate-ping"></div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 sm:space-y-4 font-mono text-[11px] sm:text-[12px] leading-relaxed scrollbar-hide">
                    {logs.length === 0 && (
                      <div className="text-gray-600 italic uppercase text-[9px] sm:text-[10px] tracking-widest text-center mt-8 sm:mt-10">Monitoring Arc Consistency...</div>
                    )}
                    {logs.map(log => (
                      <div key={log.id} className={`${log.speaker === 'model' ? 'text-dime-orange' : 'text-arc-cyan/80'}`}>
                        <div className="text-[9px] font-black opacity-30 uppercase tracking-widest mb-1">{log.speaker === 'model' ? 'DANNY' : 'YOU'}</div>
                        <div className="text-white bg-white/5 p-2 sm:p-2.5 rounded-xl inline-block">{log.text}</div>
                      </div>
                    ))}
                    <div ref={logsEndRef} />
                  </div>
                </div>
              </div>
            )}

            {isSnapping && <div className="absolute inset-0 bg-white/60 z-50 pointer-events-none animate-pulse" />}

            {/* Calibration Modal */}
            {showCalibrationModal && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
                <div className="bg-dime-card border border-white/10 rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl my-4">
                  {/* Step-by-step header */}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-black text-white mb-3">📏 Enable Measurements</h3>
                    <div className="bg-arc-cyan/10 border border-arc-cyan/30 rounded-xl p-4 mb-4">
                      <div className="flex items-start gap-3 text-left">
                        <div className="flex-shrink-0 w-6 h-6 bg-arc-cyan rounded-full flex items-center justify-center text-black font-bold text-sm">1</div>
                        <p className="text-sm text-white">Place a reference object <span className="text-arc-cyan font-bold">next to your weld</span> in the camera view</p>
                      </div>
                      <div className="flex items-start gap-3 text-left mt-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-arc-cyan rounded-full flex items-center justify-center text-black font-bold text-sm">2</div>
                        <p className="text-sm text-white">Select the matching object below</p>
                      </div>
                      <div className="flex items-start gap-3 text-left mt-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-arc-cyan rounded-full flex items-center justify-center text-black font-bold text-sm">3</div>
                        <p className="text-sm text-white">Danny will use it to give you <span className="text-green-400 font-bold">exact measurements</span></p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">Common items: coins, TIG filler rods, hex keys, or a ruler</p>
                  </div>

                  {/* TIG Rods - Universal */}
                  <div className="mb-4">
                    <div className="text-[10px] font-black text-arc-cyan uppercase tracking-widest mb-2">TIG Filler Rods</div>
                    <div className="grid grid-cols-3 gap-2">
                      {(['rod_16', 'rod_24', 'rod_32'] as const).map((key) => (
                        <button
                          key={key}
                          onClick={() => triggerCalibration(key)}
                          className="p-3 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all text-center"
                        >
                          <div className="text-lg mb-1">{REFERENCE_OBJECTS[key].icon}</div>
                          <div className="text-xs font-bold text-white">{REFERENCE_OBJECTS[key].sizeMm}mm</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tools - Universal */}
                  <div className="mb-4">
                    <div className="text-[10px] font-black text-arc-cyan uppercase tracking-widest mb-2">Tools</div>
                    <div className="grid grid-cols-4 gap-2">
                      {(['ruler_cm', 'hex_key_4', 'hex_key_5', 'hex_key_6'] as const).map((key) => (
                        <button
                          key={key}
                          onClick={() => triggerCalibration(key)}
                          className="p-3 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all text-center"
                        >
                          <div className="text-lg mb-1">{REFERENCE_OBJECTS[key].icon}</div>
                          <div className="text-[10px] font-bold text-white">{REFERENCE_OBJECTS[key].sizeMm}mm</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coins by Region */}
                  <div className="mb-4">
                    <div className="text-[10px] font-black text-arc-cyan uppercase tracking-widest mb-2">Coins</div>
                    <div className="grid grid-cols-2 gap-2">
                      {/* US */}
                      <button onClick={() => triggerCalibration('dime')} className="p-2 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all flex items-center gap-2">
                        <span className="text-sm">🇺🇸</span>
                        <div className="text-left">
                          <div className="text-[10px] font-bold text-white">Dime</div>
                          <div className="text-[9px] text-gray-500">17.91mm</div>
                        </div>
                      </button>
                      <button onClick={() => triggerCalibration('quarter')} className="p-2 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all flex items-center gap-2">
                        <span className="text-sm">🇺🇸</span>
                        <div className="text-left">
                          <div className="text-[10px] font-bold text-white">Quarter</div>
                          <div className="text-[9px] text-gray-500">24.26mm</div>
                        </div>
                      </button>
                      {/* AU */}
                      <button onClick={() => triggerCalibration('aus_dollar')} className="p-2 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all flex items-center gap-2">
                        <span className="text-sm">🇦🇺</span>
                        <div className="text-left">
                          <div className="text-[10px] font-bold text-white">$1 Coin</div>
                          <div className="text-[9px] text-gray-500">25mm</div>
                        </div>
                      </button>
                      <button onClick={() => triggerCalibration('aus_20c')} className="p-2 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all flex items-center gap-2">
                        <span className="text-sm">🇦🇺</span>
                        <div className="text-left">
                          <div className="text-[10px] font-bold text-white">20c Coin</div>
                          <div className="text-[9px] text-gray-500">28.5mm</div>
                        </div>
                      </button>
                      {/* EU */}
                      <button onClick={() => triggerCalibration('euro_1')} className="p-2 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all flex items-center gap-2">
                        <span className="text-sm">🇪🇺</span>
                        <div className="text-left">
                          <div className="text-[10px] font-bold text-white">€1 Coin</div>
                          <div className="text-[9px] text-gray-500">23.25mm</div>
                        </div>
                      </button>
                      <button onClick={() => triggerCalibration('euro_50c')} className="p-2 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all flex items-center gap-2">
                        <span className="text-sm">🇪🇺</span>
                        <div className="text-left">
                          <div className="text-[10px] font-bold text-white">€0.50 Coin</div>
                          <div className="text-[9px] text-gray-500">24.25mm</div>
                        </div>
                      </button>
                      {/* UK */}
                      <button onClick={() => triggerCalibration('gbp_pound')} className="p-2 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all flex items-center gap-2">
                        <span className="text-sm">🇬🇧</span>
                        <div className="text-left">
                          <div className="text-[10px] font-bold text-white">£1 Coin</div>
                          <div className="text-[9px] text-gray-500">23.43mm</div>
                        </div>
                      </button>
                      <button onClick={() => triggerCalibration('gbp_50p')} className="p-2 bg-white/5 border border-white/10 rounded-xl hover:border-arc-cyan hover:bg-arc-cyan/10 transition-all flex items-center gap-2">
                        <span className="text-sm">🇬🇧</span>
                        <div className="text-left">
                          <div className="text-[10px] font-bold text-white">50p Coin</div>
                          <div className="text-[9px] text-gray-500">27.3mm</div>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="bg-industrial-orange/10 border border-industrial-orange/30 rounded-xl p-3 mb-4">
                    <p className="text-[10px] text-industrial-orange font-medium">
                      <strong>Tip:</strong> Keep the reference in frame. Closer to the weld = more accurate measurements.
                    </p>
                  </div>

                  <button
                    onClick={() => setShowCalibrationModal(false)}
                    className="w-full py-3 bg-white/5 border border-white/10 rounded-xl text-gray-400 font-bold hover:bg-white/10 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </main>

          <footer className="bg-dime-card px-4 sm:px-10 py-6 sm:py-10 flex flex-wrap justify-center items-center gap-4 sm:gap-10 border-t border-white/5 shrink-0 z-20">
            <button
              onClick={toggleMic}
              className={`p-4 sm:p-6 rounded-[24px] sm:rounded-[30px] border transition-all duration-300 shadow-xl ${isMicMuted ? 'bg-red-500/20 border-red-500 text-red-500' : 'bg-gray-800/40 border-white/5 text-white hover:border-arc-cyan hover:scale-110'}`}
              title="Toggle Mic"
            >
              <svg className="w-7 h-7 sm:w-9 sm:h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={isMicMuted ? "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z M3 3l18 18" : "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"} />
              </svg>
            </button>

            <button
              onClick={() => setVideoEnabled(prev => !prev)}
              className={`p-4 sm:p-6 rounded-[24px] sm:rounded-[30px] border transition-all duration-300 shadow-xl ${videoEnabled ? 'bg-gray-800/40 border-white/5 text-white hover:border-arc-cyan hover:scale-110' : 'bg-black/60 border-red-500 text-red-400'}`}
              title="Toggle Camera"
            >
              <svg className="w-7 h-7 sm:w-9 sm:h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={videoEnabled ? "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14v2a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h8a2 2 0 012 2v2z" : "M3 5l18 18M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14v2a2 2 0 01-2 2H8"} />
              </svg>
            </button>

            <button
              onClick={cycleRoast}
              className={`p-4 sm:p-6 rounded-[24px] sm:rounded-[30px] border transition-all duration-300 shadow-xl ${roastLevel === 2 ? 'bg-red-600 border-red-400 animate-pulse' : roastLevel === 1 ? 'bg-orange-600 border-orange-400' : 'bg-gray-800/40 border-white/5 hover:border-orange-500 hover:scale-110'}`}
              title="Toggle Roast"
            >
              <svg className="w-7 h-7 sm:w-9 sm:h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.99 7.99 0 0120 13a7.98 7.98 0 01-2.343 5.657z" />
              </svg>
            </button>

            {/* Calibration Button */}
            <button
              onClick={() => calibration.isCalibrated ? clearCalibration() : setShowCalibrationModal(true)}
              className={`relative p-4 sm:p-6 rounded-[24px] sm:rounded-[30px] border transition-all duration-300 shadow-xl ${calibration.isCalibrated ? 'bg-green-600/30 border-green-500 text-green-400' : 'bg-gray-800/40 border-white/5 text-white hover:border-arc-cyan hover:scale-110'}`}
              title={calibration.isCalibrated ? `Calibrated with ${calibration.referenceObject} - Click to clear` : "Enable exact measurements (place reference object in view first)"}
            >
              <svg className="w-7 h-7 sm:w-9 sm:h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {calibration.isCalibrated && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              )}
            </button>

            <button
              onClick={cleanup}
              className="p-4 sm:p-6 rounded-[24px] sm:rounded-[30px] bg-red-600/20 border border-red-600 text-red-500 shadow-xl transition-all duration-300 hover:bg-red-600 hover:text-white hover:scale-110 active:scale-90"
              title="End Session"
            >
              <svg className="w-7 h-7 sm:w-9 sm:h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </footer>
        </>
      )}
    </div>
  );
};

export default App;























